import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BOOKING } from "@/constants/testIds/site";
import { CheckCircle2, Loader2, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const glassOptions = [
  "Shower screen / enclosure",
  "Conservatory",
  "Windows (domestic)",
  "Windows (commercial)",
  "Splashback",
  "Balustrade",
  "Other / Bespoke",
];

function formatDateISO(date) {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatPretty(date) {
  if (!date) return "Select a date";
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export default function Booking() {
  const [date, setDate] = useState(null);
  const [unavailable, setUnavailable] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    glass_type: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/bookings/unavailable-dates`)
      .then((r) => setUnavailable(r.data || []))
      .catch(() => {});
  }, []);

  const unavailableDateObjs = unavailable.map((s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  });

  const disabled = [
    { before: new Date(new Date().setHours(0, 0, 0, 0)) },
    { dayOfWeek: [0] }, // Sundays off
    ...unavailableDateObjs,
  ];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!date) return setError("Please select a date.");
    if (!form.name || !form.email || !form.phone || !form.address || !form.glass_type) {
      return setError("Please fill in all required fields.");
    }
    setSubmitting(true);
    try {
      const payload = { ...form, date: formatDateISO(date) };
      const res = await axios.post(`${API}/bookings`, payload);
      setSuccess(res.data);
      setUnavailable((u) => [...u, payload.date]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <section
        id="booking"
        data-testid={BOOKING.section}
        className="relative py-24 md:py-32 px-6 md:px-10 bg-gradient-to-b from-emerald-50/50 to-white"
      >
        <div className="max-w-3xl mx-auto text-center">
          <div
            data-testid={BOOKING.success}
            className="rounded-3xl glass p-10 md:p-14 shadow-2xl shadow-emerald-900/10"
          >
            <div className="w-16 h-16 rounded-full bg-emerald-600 text-white mx-auto flex items-center justify-center">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl font-light tracking-tight">
              Booking <span className="italic font-semibold text-emerald-700">received</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Thanks, <span className="font-semibold text-emerald-950">{success.name}</span> — we’ve
              reserved <span className="font-semibold text-emerald-950">{formatPretty(new Date(success.date))}</span> for you.
              You’ll receive a confirmation from us within 24 hours at{" "}
              <span className="font-semibold text-emerald-950">{success.email}</span>.
            </p>
            <button
              onClick={() => {
                setSuccess(null);
                setDate(null);
                setForm({ name: "", email: "", phone: "", address: "", glass_type: "", notes: "" });
              }}
              className="mt-8 px-6 py-3 rounded-full bg-emerald-950 text-white font-semibold hover:bg-emerald-700 transition-colors"
            >
              Make another booking
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="booking"
      data-testid={BOOKING.section}
      className="relative py-24 md:py-32 px-6 md:px-10 bg-gradient-to-b from-emerald-50/40 to-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-emerald-700">
            Book a visit
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
            Pick a date that <span className="italic font-semibold">works for you</span>.
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            Available dates are highlighted. We’ll confirm by phone or email
            within hours of your booking.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start"
        >
          {/* Calendar */}
          <div className="lg:col-span-5">
            <div
              data-testid={BOOKING.calendar}
              className="rounded-3xl bg-white border border-slate-200 p-5 md:p-7 shadow-sm"
            >
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays size={18} className="text-emerald-700" />
                <div className="text-sm font-semibold text-slate-700">
                  Select a date
                </div>
              </div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                disabled={disabled}
                weekStartsOn={1}
                className="mx-auto"
              />
              <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                  Chosen
                </div>
                <div
                  data-testid={BOOKING.selectedDate}
                  className="font-display text-lg font-semibold text-emerald-950"
                >
                  {formatPretty(date)}
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500">
                Sundays unavailable · Booked dates greyed out
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl bg-white border border-slate-200 p-7 md:p-10 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name *</Label>
                  <Input
                    id="name"
                    data-testid={BOOKING.name}
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Jane Doe"
                    className="h-12"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    data-testid={BOOKING.email}
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="jane@example.com"
                    className="h-12"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    data-testid={BOOKING.phone}
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="07000 000000"
                    className="h-12"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Glass type *</Label>
                  <Select
                    value={form.glass_type}
                    onValueChange={(v) => update("glass_type", v)}
                  >
                    <SelectTrigger
                      data-testid={BOOKING.glassType}
                      className="h-12"
                    >
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                    <SelectContent>
                      {glassOptions.map((o) => (
                        <SelectItem key={o} value={o}>
                          {o}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="address">Service address *</Label>
                  <Input
                    id="address"
                    data-testid={BOOKING.address}
                    value={form.address}
                    onChange={(e) => update("address", e.target.value)}
                    placeholder="House number, street, city, postcode"
                    className="h-12"
                    required
                  />
                </div>
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    data-testid={BOOKING.notes}
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Anything we should know? Access details, special requests…"
                    rows={4}
                  />
                </div>
              </div>

              {error && (
                <div
                  data-testid={BOOKING.error}
                  className="mt-5 px-4 py-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm font-medium"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid={BOOKING.submit}
                disabled={submitting}
                className="mt-7 w-full md:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-emerald-950 text-white font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>Request this date</>
                )}
              </button>

              <div className="mt-4 text-xs text-slate-500">
                No payment taken now. We’ll confirm availability and discuss
                pricing before any work begins.
              </div>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
